import { logger } from './logger';

/**
 * Custom application error class
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
  }
}

/**
 * API error class
 */
export class APIError extends AppError {
  constructor(message: string, statusCode: number, code?: string, details?: Record<string, any>) {
    super(message, code, statusCode, details);
    this.name = 'APIError';
  }
}

/**
 * WebSocket error class
 */
export class WebSocketError extends AppError {
  constructor(message: string, code?: string) {
    super(message, code);
    this.name = 'WebSocketError';
  }
}

/**
 * Extract error message from unknown error
 * @param error - Error object
 * @returns Error message string
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  return 'An unknown error occurred';
};

/**
 * Handle and log error
 * @param error - Error object
 * @param context - Error context
 */
export const handleError = (error: unknown, context?: string): void => {
  const message = getErrorMessage(error);
  const fullMessage = context ? `${context}: ${message}` : message;

  logger.error(fullMessage, error);

  // In production, you might want to send this to an error tracking service
  // e.g., Sentry, LogRocket, etc.
};

/**
 * Check if error is a network error
 * @param error - Error object
 * @returns True if network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes('Network') ||
      error.message.includes('fetch') ||
      error.message.includes('ECONNREFUSED')
    );
  }
  return false;
};

/**
 * Check if error is an authentication error
 * @param error - Error object
 * @returns True if auth error
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof APIError) {
    return error.statusCode === 401 || error.statusCode === 403;
  }
  return false;
};
